import React from 'react';

/**
 * Video Schema Markup Generator
 * Generates JSON-LD structured data for video content to improve SEO
 */

export interface VideoSchemaData {
  name: string;
  description: string;
  thumbnailUrl: string | string[];
  uploadDate: string; // ISO 8601 format (YYYY-MM-DD)
  duration?: string; // ISO 8601 duration format (e.g., "PT2M30S" for 2 minutes 30 seconds)
  contentUrl?: string; // Direct URL to video file
  embedUrl?: string; // URL for embedded player
  interactionStatistic?: {
    interactionType: 'https://schema.org/WatchAction';
    userInteractionCount: number;
  };
}

export function generateVideoSchema(data: VideoSchemaData) {
  const schema: any = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: data.name,
    description: data.description,
    thumbnailUrl: Array.isArray(data.thumbnailUrl) ? data.thumbnailUrl : [data.thumbnailUrl],
    uploadDate: data.uploadDate,
  };

  // Add optional fields
  if (data.duration) {
    schema.duration = data.duration;
  }

  if (data.contentUrl) {
    schema.contentUrl = data.contentUrl;
  }

  if (data.embedUrl) {
    schema.embedUrl = data.embedUrl;
  }

  if (data.interactionStatistic) {
    schema.interactionStatistic = data.interactionStatistic;
  }

  return schema;
}

/**
 * Generate Video JSON-LD script tag
 */
export function VideoSchemaScript({ data }: { data: VideoSchemaData }) {
  const schema = generateVideoSchema(data);

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}
